import firebaseConfig from "utils/firebaseConfig";

// Which "install the app / turn on notifications" card a coach sees at the top of the conversations list. The
// same rules as the members' website, checked in order; the first that matches wins.

// "Not now" hides every card for a week.
export const DISMISSED_KEY = "installCardDismissedAt";
export const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

export const CARDS = {
  turnOn: "turnOn",
  blocked: "blocked",
  installIOS: "installIOS",
  openInSafari: "openInSafari",
  installAndroid: "installAndroid",
  openInChrome: "openInChrome",
};

// Browsers inside other apps, which can't add to the Home Screen or get pushes.
const IOS_IN_APP =
  /Instagram|FBAN|FBAV|FB_IAB|FBIOS|MessengerForiOS|musical_ly|BytedanceWebview|TikTok|GSA\/|LinkedInApp|Snapchat|Twitter|Line\//;
const ANDROID_IN_APP = /Instagram|FBAV|FB_IAB|musical_ly|TikTok|GSA\/|; wv\)/;
// iPhone browsers other than Safari and Chrome, which hide Share in their own menu.
const IOS_OTHER_BROWSERS = /FxiOS|EdgiOS|OPiOS|OPT\/|DuckDuckGo|Ddg\/|YaBrowser/;

// iPads asking for desktop sites report themselves as a Mac, but a Mac has no touch screen.
export const isIOS = ({ userAgent, platform, maxTouchPoints }) =>
  /iPad|iPhone|iPod/.test(userAgent) || (platform === "MacIntel" && maxTouchPoints > 1);

export const isIPad = ({ userAgent, platform, maxTouchPoints }) =>
  /iPad/.test(userAgent) || (platform === "MacIntel" && maxTouchPoints > 1);

export const isAndroid = ({ userAgent }) => /Android/i.test(userAgent);

// The iOS version as [major, minor], or null when the browser doesn't say.
export const iosVersion = (userAgent) => {
  const match = userAgent.match(/OS (\d+)_(\d+)/) || userAgent.match(/Version\/(\d+)\.(\d+)/);
  return match ? [Number(match[1]), Number(match[2])] : null;
};

// Web push on iPhone arrived in iOS 16.4. An unknown version gets the card.
const iosAllowsPush = (userAgent) => {
  const version = iosVersion(userAgent);
  if (!version) return true;
  const [major, minor] = version;
  return major > 16 || (major === 16 && minor >= 4);
};

// The browser whose Share steps the iPhone guide shows.
export const iosBrowser = (userAgent) => {
  if (/CriOS/.test(userAgent)) return "chrome";
  if (IOS_OTHER_BROWSERS.test(userAgent)) return "other";
  return "safari";
};

export const deviceKind = (device) => {
  if (isIOS(device)) return "ios";
  if (isAndroid(device)) return "android";
  return "computer";
};

// device: userAgent, platform, maxTouchPoints, standalone (opened as the installed app).
// permission: "default", "granted", "denied", or "unsupported" when the browser has no web push.
// configured: this build has push settings. dismissedAt: when "Not now" was last tapped (ms), or 0.
// installPrompt: Android's install prompt is waiting to be shown.
export const installCardKind = ({
  device,
  permission,
  configured = true,
  dismissedAt = 0,
  now = Date.now(),
  installPrompt = false,
}) => {
  if (!configured) return null;
  if (permission === "granted") return null;
  // A time in the future (the clock was changed) counts as expired, so the cards aren't hidden for good.
  const dismissedFor = now - dismissedAt;
  if (dismissedAt && dismissedFor >= 0 && dismissedFor < DISMISS_MS) return null;
  if (permission === "denied") return CARDS.blocked;

  const { userAgent, standalone } = device;
  const canAsk = permission === "default";

  if (isIOS(device)) {
    if (!iosAllowsPush(userAgent)) return null;
    if (standalone) return canAsk ? CARDS.turnOn : null;
    if (IOS_IN_APP.test(userAgent)) return CARDS.openInSafari;
    return CARDS.installIOS;
  }

  if (isAndroid(device)) {
    // Phones without Google services (newer Huawei) have no push service.
    if (/HuaweiBrowser/.test(userAgent)) return null;
    if (ANDROID_IN_APP.test(userAgent)) return CARDS.openInChrome;
    if (installPrompt && !standalone) return CARDS.installAndroid;
    return canAsk ? CARDS.turnOn : null;
  }

  // Computers: no install offer.
  return canAsk ? CARDS.turnOn : null;
};

// The browser's side of the inputs above.

export const currentDevice = () => ({
  userAgent: navigator.userAgent || "",
  platform: navigator.platform || "",
  maxTouchPoints: navigator.maxTouchPoints || 0,
  standalone:
    window.navigator.standalone === true ||
    (!!window.matchMedia && window.matchMedia("(display-mode: standalone)").matches),
});

export const pushPermission = () =>
  "Notification" in window && "serviceWorker" in navigator && "PushManager" in window
    ? Notification.permission
    : "unsupported";

// Also kept in memory, so "Not now" still lasts the visit where storage is blocked.
let dismissedThisVisitAt = 0;

export const dismissedAt = () => {
  try {
    return Number(localStorage.getItem(DISMISSED_KEY)) || dismissedThisVisitAt;
  } catch (error) {
    return dismissedThisVisitAt;
  }
};

export const dismissForAWeek = () => {
  dismissedThisVisitAt = Date.now();
  try {
    localStorage.setItem(DISMISSED_KEY, String(dismissedThisVisitAt));
  } catch (error) {
    // Then it's only hidden for this visit.
  }
};

// Android's install prompt. Chrome offers it once the app is installable (after a tap and some time on the site);
// it's kept for the card's "Install app" instead of Chrome's own mini bar. A prompt can be shown only once.
let installPromptEvent = null;
const installPromptListeners = new Set();

const setInstallPrompt = (event) => {
  installPromptEvent = event;
  installPromptListeners.forEach((listener) => listener(event));
};

export const getInstallPrompt = () => installPromptEvent;

export const subscribeInstallPrompt = (listener) => {
  installPromptListeners.add(listener);
  return () => installPromptListeners.delete(listener);
};

// Used up once shown, whatever the coach chose.
export const spendInstallPrompt = () => setInstallPrompt(null);

export const startInstallPromptCapture = () => {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    setInstallPrompt(event);
  });
  window.addEventListener("appinstalled", () => setInstallPrompt(null));
};

export const currentInstallCardKind = () =>
  installCardKind({
    device: currentDevice(),
    permission: pushPermission(),
    configured: !!firebaseConfig,
    dismissedAt: dismissedAt(),
    installPrompt: !!installPromptEvent,
  });
